import { ALL_LEVELS, levelName } from "@edu/shared";
import { PICK_SUBJECTS, hasGenerators, subjectBy, type PickSubject, type TopicSubject } from "./subject.ts";

/**
 * Слова выбора одной темы.
 *
 * Отдельным модулем от разметки по правилу раздела 9 CLAUDE.md: тестов на
 * компоненты в проекте нет, а слова, которые видит преподаватель, проверить
 * надо, и проверяются они только тогда, когда живут в чистой функции.
 *
 * ⚠️ Главное правило этих строк: не обещать того, чего нет. Генераторов по
 * русскому у нас сегодня ноль, и сказать это надо словами, а не пустым списком
 * и не выключенной кнопкой без объяснения.
 *
 * ⚠️ В адрес преподавателя не считается и не пишется ничего (раздел 5.10
 * CLAUDE.md). Всё, что здесь говорится про нехватку, говорится про нас: чего
 * нет у платформы, а не чего не сделал он.
 */

/**
 * Что мешает собрать задание прямо сейчас.
 *
 * Разбор, а не текст: у состояний разные последствия. Отказу загрузки нужна
 * кнопка «Повторить», а отсутствию заданий по предмету повторять нечего, и одна
 * строка на оба случая поставила бы кнопку там, где она ничего не чинит.
 */
export type TopicHold =
  /**
   * Заданий по предмету нет. Два непохожих пути в одно место: предмет без
   * генераторов вовсе (русский, его и не спрашивают) и справочник, приехавший
   * пустым. Слова у них одни, потому что для читателя это одна и та же новость,
   * а разница между «мы не спрашивали» и «спросили, и там пусто» это наша
   * внутренняя разница.
   */
  | { readonly kind: "no-tasks"; readonly subject: PickSubject }
  | { readonly kind: "loading" }
  | { readonly kind: "failed"; readonly subject: TopicSubject }
  | { readonly kind: "no-topic" };

export interface HoldNote {
  readonly text: string;
  /** Рядом со строкой стоит «Повторить». Помогает ровно одной беде из четырёх. */
  readonly retry: boolean;
}

/**
 * Заданий по предмету нет, зато есть по другим.
 *
 * ⚠️ Перечень предметов СЧИТАЕТСЯ, а не выписан словами. Выпиши его руками, и
 * первый же приход генераторов по русскому оставит на экране строку «Есть по
 * математике и по английскому» ровно там, где задания уже есть.
 */
function noTasksText(subject: PickSubject, allowed: readonly PickSubject[]): string {
  const others = allowed
    .filter((s) => s !== subject && hasGenerators(s))
    .map(subjectBy)
    .join(" и ");
  const head = `Заданий ${subjectBy(subject)} у нас пока нет.`;
  return others === "" ? head : `${head} Есть ${others}.`;
}

/**
 * `allowed` это предметы, которые человек вправе выбрать в этом пикере
 * (`pickSubjectsOf` в комнате). ⚠️ Строка «Есть по английскому» у того, кому
 * английский не показан, отправила бы его искать переключатель, которого нет.
 */
export function holdNote(hold: TopicHold, allowed: readonly PickSubject[] = PICK_SUBJECTS): HoldNote {
  switch (hold.kind) {
    case "no-tasks":
      return { text: noTasksText(hold.subject, allowed), retry: false };
    case "loading":
      return { text: "Загрузка тем…", retry: false };
    case "failed":
      // Слово в слово как в форме выдачи домашки: одна и та же беда, названная
      // двумя способами, читается как две разные
      return { text: `Темы ${subjectBy(hold.subject)} не загрузились.`, retry: true };
    case "no-topic":
      /*
        ⚠️ Не «выберите тему»: ровно эти слова стоят пустым пунктом в самом
        списке, и повторять их значит показывать пальцем на соседа. Строка
        отвечает на другой вопрос, тот, ради которого её и завели: почему
        кнопка не нажимается.
      */
      return { text: "Задание соберётся после выбора темы.", retry: false };
  }
}

/**
 * «лёгкий и средний»: перечисление уровней словами, а не цифрами.
 *
 * ⚠️ Живёт здесь, а не у строки-пика домашки, потому что читают его двое, и
 * второй экран это комната урока. Вторая копия перечисления разъехалась бы на
 * первой же правке союза.
 */
export function levelsLine(available: readonly number[]): string {
  const names = available.map(levelName);
  if (names.length <= 1) return names.join("");
  const last = names[names.length - 1];
  return `${names.slice(0, -1).join(", ")} и ${last}`;
}

/**
 * Подпись сегмента «любой» у переключателя сложности.
 *
 * ⚠️ Мужской род, как у соседей по переключателю («лёгкий», «средний»,
 * «сложный»): все четыре подписи это один и тот же подразумеваемый уровень, и
 * «любая» посреди трёх «-ых» читается как подпись к чему-то другому. Что группа
 * называется «Сложность», роли не играет: «Сложность: лёгкий» стоит там с
 * первого дня.
 */
export const ANY_LEVEL_NAME = "любой";

/**
 * Тема, у которой уровень ОДИН: переключателя нет вовсе, вместо него строка.
 *
 * ⚠️ Три сегмента, из которых два выключены, это шум, притворяющийся выбором.
 * Слова «уровень» в строке нет намеренно: рядом стоит уровень материала
 * («Материал: уровень B1»), а это другая шкала.
 */
export function singleLevelLine(difficulty: number): string {
  return `сложность: только ${levelName(difficulty)}`;
}

/**
 * У темы есть не все уровни: назвать те, что есть.
 *
 * `null` в двух случаях, и оба означают «сказать нечего»: уровень единственный
 * (о нём говорит `singleLevelLine`) либо есть все три, и тогда выключенных
 * сегментов нет. ⚠️ Выключенный сегмент без объяснения читается как поломка,
 * поэтому молчать можно только тогда, когда выключенных нет.
 */
export function onlyLevelsNote(available: readonly number[]): string | null {
  if (available.length <= 1 || available.length >= ALL_LEVELS.length) return null;
  return `По этой теме есть только ${levelsLine(available)}.`;
}
